// src/utils/api.js
import { CSRF_HEADER, getCsrfTokenFor, shouldAttachCsrf } from './csrfFetch';
import { getApiBase, getBackendNavigationBase } from './apiBase';
import { markFocusRequest, notifyFocusDenied } from './focusPilotTransport';

export function buildUrl(path) {
  const apiBase = getApiBase();

  if (!path) return apiBase;
  if (/^https?:\/\//i.test(path)) return path;
  if (!apiBase) return path;
  return `${apiBase}${path.startsWith('/') ? path : `/${path}`}`;
}

export function buildBackendUrl(path) {
  const apiBase = getBackendNavigationBase();

  if (!path) return apiBase;
  if (/^https?:\/\//i.test(path)) return path;
  return `${apiBase}${path.startsWith('/') ? path : `/${path}`}`;
}

function isBodyWithBrowserManagedContentType(body) {
  return (
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)
  );
}

async function parseResponse(res) {
  if (res.status === 204) return null;

  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  return res.text();
}

function getFetch() {
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    return window.fetch.bind(window);
  }

  throw new Error('Fetch API is not available.');
}

// Centralized fetch wrapper that:
// - always sends credentials
// - routes relative API calls to the configured backend URL in prod
// - removes Content-Type when body is FormData (so the browser can set the boundary)
// - sets JSON headers only for non-FormData bodies
// - returns JSON when possible, else text
// - surfaces useful error text on non-2xx
export async function apiFetch(path, options = {}) {
  const url = buildUrl(path);
  const { responseType, ...fetchOptions } = options;
  const opts = { ...fetchOptions, credentials: 'include' };
  const method = String(opts.method || 'GET').toUpperCase();

  // Normalize headers
  const headers = new Headers(opts.headers || {});
  if (typeof window !== 'undefined') markFocusRequest(path, headers);

  // If body is FormData, DO NOT set Content-Type (browser adds multipart boundary)
  if (isBodyWithBrowserManagedContentType(opts.body)) {
    headers.delete('Content-Type');
  } else if (opts.body != null && typeof opts.body === 'object') {
    // If a plain object is passed to apiFetch directly, JSONify it
    // (postJSON/patchJSON already do this, but this keeps apiFetch safe if used elsewhere)
    if (!isBodyWithBrowserManagedContentType(opts.body)) {
      if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
      // Only stringify if not already a string
      if (typeof opts.body !== 'string') {
        opts.body = JSON.stringify(opts.body);
      }
    }
  }

  if (shouldAttachCsrf(url, method) && !headers.has(CSRF_HEADER)) {
    headers.set(CSRF_HEADER, (await getCsrfTokenFor(url)) || '');
  }

  opts.headers = headers;

  const res = await getFetch()(url, opts);

  if (!res.ok) {
    let detail;
    try {
      const data = await parseResponse(res);
      if (data && typeof data === 'object') {
        notifyFocusDenied(data?.error?.code);
        // Common error shapes:
        // { error: 'x' }
        // { message: 'x' }
        // { error: { message: 'x' } }
        detail =
          (typeof data.error === 'string' && data.error) ||
          data?.error?.message ||
          data.message ||
          JSON.stringify(data);
      } else {
        detail = String(data);
      }
    } catch {
      detail = `${res.status} ${res.statusText}`;
    }
    const err = new Error(detail || `${res.status} ${res.statusText}`);
    err.status = res.status;
    err.statusText = res.statusText;
    const retryAfter = res.headers.get('Retry-After');
    if (retryAfter) {
      const seconds = Number(retryAfter);
      const milliseconds = Number.isFinite(seconds)
        ? seconds * 1000
        : Date.parse(retryAfter) - Date.now();
      if (Number.isFinite(milliseconds)) err.retryAfterMs = Math.max(0, milliseconds);
    }
    throw err;
  }

  return responseType === 'blob' ? res.blob() : parseResponse(res);
}

// === Public helpers (keep existing signatures) ===

export async function fetchJSON(path) {
  return apiFetch(path);
}

export async function postJSON(path, body) {
  return apiFetch(path, {
    method: 'POST',
    body, // can be a plain object or FormData (guard above handles both)
  });
}

export async function patchJSON(path, body) {
  return apiFetch(path, {
    method: 'PATCH',
    body, // can be a plain object or FormData (guard above handles both)
  });
}

export async function putJSON(path, body) {
  return apiFetch(path, {
    method: 'PUT',
    body,
  });
}

export async function del(path) {
  return apiFetch(path, { method: 'DELETE' });
}

// Optional: explicit helper for uploads if you ever want it.
// Usage: uploadForm('/api/organization/logo', formData, 'PATCH')
export function uploadForm(path, formData, method = 'POST') {
  return apiFetch(path, { method, body: formData });
}
